import { useState, useEffect } from 'react';
import { readFullDayHoursByBranch } from '../services/branchSchedule.service.ts';

const CACHE_TTL_MS = 10 * 60 * 1000;
const fullDayHoursCache = new Map<
  number,
  { value: number; expiresAt: number }
>();
const inflight = new Map<number, Promise<number>>();

export async function getFullDayHoursForBranch(
  branchId: number
): Promise<number> {
  const cached = fullDayHoursCache.get(branchId);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.value;
  }

  let request = inflight.get(branchId);
  if (!request) {
    request = (async () => {
      try {
        const response = await readFullDayHoursByBranch(branchId);
        let value = 8.5;
        if (
          response?.fullDayHours !== undefined &&
          response?.fullDayHours !== null
        ) {
          const parsedValue =
            typeof response.fullDayHours === 'string'
              ? parseFloat(response.fullDayHours)
              : Number(response.fullDayHours);
          if (!isNaN(parsedValue) && parsedValue > 0) {
            value = parsedValue;
          }
        }
        fullDayHoursCache.set(branchId, {
          value,
          expiresAt: Date.now() + CACHE_TTL_MS,
        });
        return value;
      } catch (error) {
        console.warn(
          `Error obteniendo fullDayHours para branch ${branchId}, usando default:`,
          error
        );
        return 8.5;
      } finally {
        inflight.delete(branchId);
      }
    })();
    inflight.set(branchId, request);
  }

  return request;
}

/**
 * Horas de jornada completa por sucursal (cache en memoria; una sola petición HTTP por branch).
 */
export const useFullDayHours = (
  branchId: number | undefined,
  prefetched?: number
) => {
  const cached =
    branchId != null ? fullDayHoursCache.get(branchId) : undefined;
  const initial =
    prefetched != null && prefetched > 0
      ? prefetched
      : cached && cached.expiresAt > Date.now()
        ? cached.value
        : 8.5;

  const [fullDayHours, setFullDayHours] = useState<number>(initial);
  const [loading, setLoading] = useState<boolean>(
    prefetched == null && branchId != null
  );

  useEffect(() => {
    if (prefetched != null && prefetched > 0) {
      setFullDayHours(prefetched);
      setLoading(false);
      return;
    }

    if (!branchId) {
      setFullDayHours(8.5);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getFullDayHoursForBranch(branchId)
      .then((value) => {
        if (!cancelled) setFullDayHours(value);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [branchId, prefetched]);

  return { fullDayHours, loading };
};
